import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useAdminStore } from '../../store/adminStore';

// Tab Component
const TabButton = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 font-medium text-sm rounded-t-lg transition-colors ${
      active
        ? 'bg-white text-blue-600 border-t border-l border-r border-gray-200'
        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
    }`}
  >
    {children}
  </button>
);

// User Management Component
const UserManagement = () => {
  const { users, roles, isLoading, fetchUsers, fetchRoles, createUser, updateUser, deleteUser } = useAdminStore();
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [formData, setFormData] = useState({ username: '', email: '', password: '', role_ids: [] });

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editUser) {
        const updateData = { email: formData.email, role_ids: formData.role_ids };
        if (formData.password) updateData.password = formData.password;
        await updateUser(editUser.id, updateData);
      } else {
        await createUser(formData);
      }
      setShowModal(false);
      setEditUser(null);
      setFormData({ username: '', email: '', password: '', role_ids: [] });
    } catch (error) {
      alert(error.message);
    }
  };

  const handleEdit = (user) => {
    setEditUser(user);
    setFormData({
      username: user.username,
      email: user.email || '',
      password: '',
      role_ids: user.roles?.map(r => r.id) || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        await deleteUser(userId);
      } catch (error) {
        alert(error.message);
      }
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">사용자 목록</h2>
        <button
          onClick={() => { setEditUser(null); setFormData({ username: '', email: '', password: '', role_ids: [] }); setShowModal(true); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          사용자 추가
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-8">로딩 중...</div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">사용자명</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">이메일</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">역할</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">마지막 로그인</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">작업</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.id}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.username}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.roles || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs rounded-full ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {user.is_active ? '활성' : '비활성'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {user.last_login ? new Date(user.last_login).toLocaleString('ko-KR') : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button onClick={() => handleEdit(user)} className="text-blue-600 hover:text-blue-900 mr-3">수정</button>
                  <button onClick={() => handleDelete(user.id)} className="text-red-600 hover:text-red-900">삭제</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">{editUser ? '사용자 수정' : '사용자 추가'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">사용자명</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    disabled={!!editUser}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 disabled:bg-gray-100"
                    required={!editUser}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">이메일</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    비밀번호 {editUser && '(변경 시에만 입력)'}
                  </label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required={!editUser}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">역할</label>
                  <div className="mt-2 space-y-2">
                    {roles.map((role) => (
                      <label key={role.id} className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.role_ids.includes(role.id)}
                          onChange={(e) => {
                            const newRoleIds = e.target.checked
                              ? [...formData.role_ids, role.id]
                              : formData.role_ids.filter(id => id !== role.id);
                            setFormData({ ...formData, role_ids: newRoleIds });
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">{role.name} - {role.description}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
                  취소
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  {editUser ? '수정' : '생성'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Role Management Component
const RoleManagement = () => {
  const { roles, permissions, isLoading, fetchRoles, fetchPermissions, createRole, updateRole, deleteRole } = useAdminStore();
  const [showModal, setShowModal] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', permission_ids: [] });

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editRole) {
        await updateRole(editRole.id, formData);
      } else {
        await createRole(formData);
      }
      setShowModal(false);
      setEditRole(null);
      setFormData({ name: '', description: '', permission_ids: [] });
    } catch (error) {
      alert(error.message);
    }
  };

  const handleEdit = async (role) => {
    setEditRole(role);
    setFormData({
      name: role.name,
      description: role.description || '',
      permission_ids: role.permissions?.map(p => p.id) || [],
    });
    setShowModal(true);
  };

  const handleDelete = async (roleId) => {
    if (window.confirm('정말 삭제하시겠습니까?')) {
      try {
        await deleteRole(roleId);
      } catch (error) {
        alert(error.message);
      }
    }
  };

  // Group permissions by resource
  const groupedPermissions = permissions.reduce((acc, perm) => {
    if (!acc[perm.resource]) acc[perm.resource] = [];
    acc[perm.resource].push(perm);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">역할 목록</h2>
        <button
          onClick={() => { setEditRole(null); setFormData({ name: '', description: '', permission_ids: [] }); setShowModal(true); }}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          역할 추가
        </button>
      </div>

      {isLoading ? (
        <div className="text-center py-8">로딩 중...</div>
      ) : (
        <div className="grid gap-4">
          {roles.map((role) => (
            <div key={role.id} className="bg-white border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="font-semibold text-gray-900">{role.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">{role.description}</p>
                  <div className="mt-2">
                    <span className="text-xs text-gray-400">권한: </span>
                    <span className="text-xs text-gray-600">{role.permissions || '없음'}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(role)} className="text-blue-600 hover:text-blue-900 text-sm">수정</button>
                  {role.id !== 1 && (
                    <button onClick={() => handleDelete(role.id)} className="text-red-600 hover:text-red-900 text-sm">삭제</button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Role Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">{editRole ? '역할 수정' : '역할 추가'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">역할명</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">설명</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">권한</label>
                  {Object.entries(groupedPermissions).map(([resource, perms]) => (
                    <div key={resource} className="mb-3">
                      <h4 className="text-sm font-medium text-gray-600 capitalize mb-1">{resource}</h4>
                      <div className="ml-4 space-y-1">
                        {perms.map((perm) => (
                          <label key={perm.id} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.permission_ids.includes(perm.id)}
                              onChange={(e) => {
                                const newPermIds = e.target.checked
                                  ? [...formData.permission_ids, perm.id]
                                  : formData.permission_ids.filter(id => id !== perm.id);
                                setFormData({ ...formData, permission_ids: newPermIds });
                              }}
                              className="mr-2"
                            />
                            <span className="text-sm">{perm.name} - {perm.description}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300">
                  취소
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  {editRole ? '수정' : '생성'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Audit Log Component
const AuditLog = () => {
  const { auditLogs, isLoading, fetchAuditLogs } = useAdminStore();

  useEffect(() => {
    fetchAuditLogs({ limit: 100 });
  }, []);

  return (
    <div>
      <h2 className="text-lg font-semibold mb-4">감사 로그</h2>
      {isLoading ? (
        <div className="text-center py-8">로딩 중...</div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">시간</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">사용자</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">작업</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">리소스</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">IP</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상세</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {auditLogs.map((log) => (
              <tr key={log.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(log.created_at).toLocaleString('ko-KR')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{log.username || '-'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded">{log.action}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {log.resource ? `${log.resource}/${log.resource_id || ''}` : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{log.ip_address || '-'}</td>
                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{log.details || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

// Main Admin Component
export default function Admin() {
  const { hasPermission } = useAuthStore();
  const [activeTab, setActiveTab] = useState('users');

  // 권한 체크
  const canManageUsers = hasPermission('admin:users');
  const canManageRoles = hasPermission('admin:roles');
  const canViewAudit = hasPermission('admin:audit');

  if (!canManageUsers && !canManageRoles && !canViewAudit) {
    return (
      <div className="py-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">관리자</h1>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-700">이 페이지에 접근할 권한이 없습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">관리자</h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-0">
        {canManageUsers && (
          <TabButton active={activeTab === 'users'} onClick={() => setActiveTab('users')}>
            사용자 관리
          </TabButton>
        )}
        {canManageRoles && (
          <TabButton active={activeTab === 'roles'} onClick={() => setActiveTab('roles')}>
            역할 관리
          </TabButton>
        )}
        {canViewAudit && (
          <TabButton active={activeTab === 'audit'} onClick={() => setActiveTab('audit')}>
            감사 로그
          </TabButton>
        )}
      </div>

      {/* Tab Content */}
      <div className="bg-white shadow rounded-b-lg rounded-tr-lg p-6 border border-gray-200">
        {activeTab === 'users' && canManageUsers && <UserManagement />}
        {activeTab === 'roles' && canManageRoles && <RoleManagement />}
        {activeTab === 'audit' && canViewAudit && <AuditLog />}
      </div>
    </div>
  );
}
